import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDepressionMode } from '../contexts/DepressionModeContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import ReactMarkdown from 'react-markdown';
import api from '../api/api';
import '../styles/pages/SavedRecipesPage.css';

/**
 * 保存レシピ一覧ページ
 * つむぎデザインシステム準拠（BEM記法）
 * DEPRESSION_MODE_REDESIGN.md準拠: おやすみモード時はフィルター・詳細非表示
 */
const SavedRecipesPage = () => {
  const navigate = useNavigate();
  const { depressionMode } = useDepressionMode();

  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState('all');

  useEffect(() => {
    document.title = '保存レシピ | はしもと栄養パートナー';
    fetchRecipes();
  }, [selectedTag]);

  const fetchRecipes = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      if (selectedTag !== 'all') {
        params.append('tag', selectedTag);
      }

      const response = await api.get(`/api/recipes?${params.toString()}`);

      if (response.data.success) {
        setRecipes(response.data.recipes);
      } else {
        console.error('レシピ取得エラー:', response.data.message);
      }
    } catch (error) {
      console.error('レシピ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteRecipe = async (recipeId) => {
    if (!window.confirm('このレシピを削除しますか？')) {
      return;
    }

    try {
      const response = await api.delete(`/api/recipes/${recipeId}`);

      if (response.data.success) {
        alert('✅ レシピを削除しました');
        fetchRecipes();
      } else {
        alert('❌ レシピの削除に失敗しました: ' + response.data.message);
      }
    } catch (error) {
      console.error('レシピ削除エラー:', error);
      alert('❌ レシピの削除に失敗しました');
    }
  };

  const toggleFavorite = async (recipeId) => {
    try {
      const response = await api.patch(`/api/recipes/${recipeId}/favorite`);

      if (response.data.success) {
        fetchRecipes();
      } else {
        alert('❌ お気に入りの更新に失敗しました: ' + response.data.message);
      }
    } catch (error) {
      console.error('お気に入り更新エラー:', error);
      alert('❌ お気に入りの更新に失敗しました');
    }
  };

  const openRecipeDetail = (recipe) => {
    // 認証付きレシピ詳細ページに遷移
    navigate(`/recipes/${recipe._id}`);
  };

  // レシピをシェア（共有URL作成）
  const handleShareRecipe = async (recipe) => {
    try {
      console.log('📤 シェア開始:', recipe._id);

      // 共有URLを作成
      const response = await api.post(`/api/recipes/${recipe._id}/share`);
      console.log('✅ API response:', response.data);

      if (response.data.success) {
        const shareUrl = response.data.shareUrl;
        console.log('🔗 Share URL:', shareUrl);

        // Web Share APIに対応しているかチェック
        if (navigator.share) {
          console.log('📱 Using Web Share API');
          await navigator.share({
            title: recipe.title,
            text: `「${recipe.title}」のレシピをチェック！`,
            url: shareUrl
          });
        } else {
          console.log('📋 Using clipboard');
          // 非対応の場合はクリップボードにコピー
          await navigator.clipboard.writeText(shareUrl);
          alert(`📋 共有URLをコピーしました！\n${shareUrl}`);
        }
      } else {
        console.error('❌ API returned success: false', response.data);
        alert(`❌ 共有URLの作成に失敗しました: ${response.data.message || '不明なエラー'}`);
      }
    } catch (err) {
      // ユーザーがキャンセルした場合などはエラーを無視
      if (err.name !== 'AbortError') {
        console.error('❌ シェアエラー:', err);
        console.error('Error details:', err.response?.data || err.message);
        alert(`❌ シェアに失敗しました: ${err.response?.data?.message || err.message}`);
      }
    }
  };

  const filterTags = [
    { id: 'all', label: 'すべて', emoji: '🍽️' },
    { id: '朝食', label: '朝食', emoji: '🌅' },
    { id: '昼食', label: '昼食', emoji: '☀️' },
    { id: '夕食', label: '夕食', emoji: '🌙' },
    { id: 'ヨウ素控えめ', label: 'ヨウ素控えめ', emoji: '🌊' },
    { id: 'グルテンフリー', label: 'グルテンフリー', emoji: '🌾' },
    { id: '簡単', label: '簡単', emoji: '⚡' },
    { id: 'セレン', label: 'セレン', emoji: '💎' }
  ];

  return (
    <AppLayout>
      <PageTitle
        title="保存レシピ"
        subtitle="つむぎが提案したレシピをまとめて確認"
        icon="💾"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      <div className="saved-recipes-page">
        <div className="saved-recipes-page__container">
          {/* レシピ生成リンク - おやすみモードでは非表示 */}
          {!depressionMode && (
            <div className="saved-recipes-page__quick-action">
              <button
                className="saved-recipes-page__generate-link"
                onClick={() => navigate('/recipe-hub')}
              >
                🔍 新しいレシピを探す →
              </button>
            </div>
          )}

          {/* タグフィルター - おやすみモードでは非表示 */}
          {!depressionMode && (
          <div className="saved-recipes-page__filters">
            {filterTags.map(tag => (
              <button
                key={tag.id}
                className={`saved-recipes-page__filter ${selectedTag === tag.id ? 'saved-recipes-page__filter--active' : ''}`}
                onClick={() => setSelectedTag(tag.id)}
              >
                <span className="saved-recipes-page__filter-emoji">{tag.emoji}</span>
                <span className="saved-recipes-page__filter-label">{tag.label}</span>
              </button>
            ))}
          </div>
          )}

          {/* レシピ一覧 */}
          {loading ? (
            <div className="saved-recipes-page__loading">
              <div className="saved-recipes-page__loading-spinner"></div>
              <p>読み込み中...</p>
            </div>
          ) : recipes.length === 0 ? (
            <div className="saved-recipes-page__empty">
              <div className="saved-recipes-page__empty-icon flex justify-center">
                <img src="/tsumugi.png" alt="つむぎ" className="saved-recipes-page__tsumugi-image" />
              </div>
              <h3 className="saved-recipes-page__empty-title">
                {selectedTag === 'all' ? 'レシピがまだ保存されていません' : 'このタグのレシピはありません'}
              </h3>
              <p className="saved-recipes-page__empty-text">
                {selectedTag === 'all'
                  ? 'レシピを生成して保存してみましょう'
                  : '他のタグを試してみてください'}
              </p>
              {selectedTag === 'all' && (
                <button
                  className="saved-recipes-page__empty-cta"
                  onClick={() => navigate('/recipe-hub')}
                >
                  レシピを作る
                </button>
              )}
            </div>
          ) : (
            <div className="saved-recipes-page__grid">
              {recipes.map(recipe => (
                <div key={recipe._id} className="saved-recipes-page__card">
                  {/* お気に入りボタン - おやすみモードでは非表示 */}
                  {!depressionMode && (
                  <button
                    className={`saved-recipes-page__pin ${recipe.isFavorite ? 'saved-recipes-page__pin--active' : ''}`}
                    onClick={() => toggleFavorite(recipe._id)}
                    title={recipe.isFavorite ? 'ピン留めを外す' : 'ピン留めする'}
                  >
                    📌
                  </button>
                  )}

                  {/* レシピヘッダー */}
                  <div className="saved-recipes-page__card-header">
                    <h3 className="saved-recipes-page__card-title">{recipe.title}</h3>
                    {/* タグ表示 - おやすみモードでは非表示 */}
                    {!depressionMode && recipe.tags && recipe.tags.length > 0 && (
                      <div className="saved-recipes-page__tags">
                        {recipe.tags.slice(0, 2).map((tag, idx) => (
                          <span key={idx} className="saved-recipes-page__tag">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* レシピプレビュー - おやすみモードでは非表示 */}
                  {!depressionMode && (
                  <div className="saved-recipes-page__preview">
                    <ReactMarkdown>
                      {recipe.content.substring(0, 150) + "..."}
                    </ReactMarkdown>
                  </div>
                  )}

                  {/* レシピ統計 - おやすみモードでは非表示 */}
                  {!depressionMode && recipe.triedCount > 0 && (
                    <div className="saved-recipes-page__stats">
                      <span className="saved-recipes-page__stats-icon">🍴</span>
                      <span>{recipe.triedCount}回作りました</span>
                    </div>
                  )}

                  {/* アクションボタン */}
                  <div className="saved-recipes-page__actions">
                    <button
                      className="saved-recipes-page__btn saved-recipes-page__btn--primary"
                      onClick={() => openRecipeDetail(recipe)}
                    >
                      詳細を見る
                    </button>
                    {/* 共有・削除ボタン - おやすみモードでは非表示 */}
                    {!depressionMode && (
                    <>
                    <button
                      className="saved-recipes-page__btn-share"
                      onClick={() => handleShareRecipe(recipe)}
                      title="LINE・メール・SNSなどでレシピをシェア"
                    >
                      📤
                    </button>
                    <button
                      className="saved-recipes-page__btn-delete"
                      onClick={() => deleteRecipe(recipe._id)}
                      title="削除"
                    >
                      🗑️
                    </button>
                    </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </AppLayout>
  );
};

export default SavedRecipesPage;
